import clsx from "clsx"
import { forwardRef } from "react"

import useDebouncedCallback from "@hooks/useDebouncedCallback"

import { Skeleton, TruncatableText } from "@particles/index"

import Icon from "@atoms/Icon/Icon"

import styles from "./LinkButton.module.scss"
import { LinkButtonProps } from "./LinkButton.types"

function LinkButton(props: LinkButtonProps, ref: React.Ref<HTMLButtonElement>) {
    const {
        children,
        colorScheme,
        type = "button",
        className,

        isTabbable = true,
        tooltipOnTruncate = false,

        leftIcon,
        leftIconClassName,
        rightIcon,
        rightIconClassName,

        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isDisabled = false,
        isLoading = false,

        onClick,
        noDebounce = false,

        ...rest
    } = props

    const debouncedClick = useDebouncedCallback(onClick)

    const iconSize = 14

    return (
        <button
            className={clsx(
                styles.base,
                className,
                {
                    [styles.isLoading]: isLoading,
                    [styles.isFocused]: _isFocused,
                    [styles.isHovered]: _isHovered,
                    [styles.isPressed]: isPressed,
                    [styles.isDisabled]: isDisabled,
                    [styles.truncatable]: tooltipOnTruncate,
                },
                styles[`${colorScheme}ColorScheme`],
            )}
            type={type}
            onClick={noDebounce ? onClick : debouncedClick}
            disabled={isDisabled || isLoading}
            aria-disabled={isDisabled}
            aria-busy={isLoading}
            aria-pressed={isPressed}
            tabIndex={isTabbable && !isDisabled ? 0 : -1}
            ref={ref}
            {...rest}
        >
            <div className={styles.content}>
                {leftIcon &&
                    (isLoading ? (
                        <div className={styles.iconSkeleton}>
                            <Skeleton circle={true} height={iconSize} width={iconSize} enableAnimation={true} />
                        </div>
                    ) : (
                        <Icon container="fixedHeight" icon={leftIcon} size={iconSize} className={leftIconClassName} />
                    ))}

                {isLoading ? (
                    <div className={styles.contentSkeleton}>
                        <Skeleton height={10} width={40} borderRadius={0} enableAnimation={true} />
                    </div>
                ) : tooltipOnTruncate && typeof children === "string" ? (
                    <TruncatableText
                        className={styles.truncatableLabel}
                        text={children}
                        tooltipOnTruncate={true}
                        tooltipPlacement="top"
                    />
                ) : (
                    children
                )}

                {rightIcon &&
                    (isLoading ? (
                        <div className={styles.iconSkeleton}>
                            <Skeleton circle={true} height={iconSize} width={iconSize} enableAnimation={true} />
                        </div>
                    ) : (
                        <Icon
                            container="fixedHeight"
                            icon={rightIcon}
                            size={iconSize}
                            className={rightIconClassName}
                        />
                    ))}
            </div>
        </button>
    )
}

export default forwardRef(LinkButton)
